import { BaseloadAnalysisFeatureService } from '@/features/baseload-analysis/baseload-analysis-feature.service';
import { CustomBusinessIntelligenceAnalysisFeatureService } from '@/features/custom-business-intelligence-analysis/custom-business-intelligence-analysis-feature.service';
import { EnergySignatureFeatureService } from '@/features/energy-signature/energy-signature-feature.service';
import { ThermalComfortFeatureService } from '@/features/thermal-comfort/thermal-comfort-feature.service';
import type { BusinessIntelligenceDataSourceCode, BusinessIntelligenceFilter } from '@/interfaces/BusinessIntelligence.interface';
import type { ClassificationNodeId } from '@/interfaces/Classification.interface';
import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { UiChoiceModalComponent, type UiChoiceModalInput } from '@/ui/choice-modal/ui-choice-modal.component';
import { inject, Injectable } from '@angular/core';
import { type CanActivateChildFn, type CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, type Observable } from 'rxjs';

type AnalysisType = 'custom' | 'baseload';

@Injectable({
    providedIn: 'root'
})
export class DataboardsFeatureService {
    private _baseloadAnalysisFeature = inject(BaseloadAnalysisFeatureService);
    private _customBiAnalysisFeature = inject(CustomBusinessIntelligenceAnalysisFeatureService);
    private _energySignatureFeature = inject(EnergySignatureFeatureService);
    private _thermalComfortFeature = inject(ThermalComfortFeatureService);
    private _router = inject(Router);
    private _modalService = inject(AdvizeoModalService);
    private _translate = inject(TranslateService);

    goToFeature() {
        this._router.navigate(['/', 'databoards']);
    }

    hasFeatureAccess$(): Observable<boolean> {
        return combineLatest([
            this._baseloadAnalysisFeature.hasFeatureAccess$(),
            this._customBiAnalysisFeature.hasFeatureAccess$(),
            this._energySignatureFeature.hasFeatureAccess$(),
            this._thermalComfortFeature.hasFeatureAccess$()
        ]).pipe(map(accesses => accesses.some(hasAccess => hasAccess)));
    }

    async hasFeatureAccess(): Promise<boolean> {
        return (
            (await this._baseloadAnalysisFeature.hasFeatureAccess()) ||
            (await this._customBiAnalysisFeature.hasFeatureAccess()) ||
            (await this._energySignatureFeature.hasFeatureAccess()) ||
            (await this._thermalComfortFeature.hasFeatureAccess())
        );
    }

    canStartAnalysis$(): Observable<boolean> {
        return this.hasFeatureAccess$();
    }

    async startAnalysis(options?: {
        classificationNodeId?: ClassificationNodeId;
        filters?: { dataSource: BusinessIntelligenceDataSourceCode; value: BusinessIntelligenceFilter[] };
    }) {
        const modal = this._modalService.openAdvizeoModal<UiChoiceModalInput<AnalysisType>, AnalysisType>(UiChoiceModalComponent, {
            title: this._translate.instant('PAGES.BASELOAD_ANALYSIS.GO_TO_PAGE'),
            subTitle: this._translate.instant('PAGES.BASELOAD_ANALYSIS.ANALYSIS_CHOICE'),
            buttons: [
                ...((await this._customBiAnalysisFeature.hasFeatureAccess())
                    ? [
                          {
                              text: this._translate.instant('PAGES.BASELOAD_ANALYSIS.ANALYSIS_TYPE.CUSTOM'),
                              code: 'custom' as const
                          }
                      ]
                    : []),
                ...((await this._baseloadAnalysisFeature.hasFeatureAccess())
                    ? [
                          {
                              text: this._translate.instant('PAGES.BASELOAD_ANALYSIS.ANALYSIS_TYPE.BASELOAD_ANALYSIS'),
                              code: 'baseload' as const
                          }
                      ]
                    : [])
            ]
        });

        const analysisType = await modal.afterClosed();

        if (!analysisType) {
            return;
        }

        if (analysisType === 'baseload') {
            this._baseloadAnalysisFeature.goToFeature(options ?? {});
        } else {
            this._customBiAnalysisFeature.goToFeature(options ?? {});
        }
    }
}

export const databoardsGuard: CanActivateFn & CanActivateChildFn = async () => {
    const featureService = inject(DataboardsFeatureService);
    const hasFeatureAccess = await featureService.hasFeatureAccess();

    if (!hasFeatureAccess) {
        // redirect ?
        return false;
    }
    return true;
};
